import { useEffect, useState } from 'react'
import { ShieldCheck, Loader2, Smartphone, Trash2 } from 'lucide-react'
import { authApi } from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

// OSPROD-180: Settings → Security SMS two-factor controls. Enable requires
// a verified phone (OSPROD-179). Disable requires the password + an SMS
// code (per spec). Trusted devices are listed + revocable.

interface TrustedDevice {
  id: string
  label: string | null
  createdAt: string
  lastUsedAt: string
  expiresAt: string
}

type DisablePhase = 'idle' | 'password' | 'code'

export default function TwoFactorSection() {
  const user = useAuthStore((s) => s.user)
  const fetchUser = useAuthStore((s) => s.fetchUser)
  const enabled = !!user?.twoFactorEnabled
  const phoneVerified = !!user?.phoneVerified

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const [disablePhase, setDisablePhase] = useState<DisablePhase>('idle')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')

  const [devices, setDevices] = useState<TrustedDevice[]>([])

  const loadDevices = () => {
    if (!enabled) { setDevices([]); return }
    authApi.twoFactor.listTrustedDevices()
      .then((res) => setDevices(res.data.devices))
      .catch(() => {})
  }
  useEffect(() => { loadDevices() }, [enabled])

  const handleEnable = async () => {
    setError(''); setInfo(''); setBusy(true)
    try {
      await authApi.twoFactor.enable()
      await fetchUser()
      setInfo('Two-factor authentication is on.')
    } catch (err: any) {
      if (err?.response?.data?.error === 'phone_not_verified') setError('Verify your phone number in the Profile tab first.')
      else setError('Could not enable 2FA. Please try again.')
    } finally { setBusy(false) }
  }

  const handleDisableStart = async () => {
    setError(''); setInfo(''); setBusy(true)
    try {
      const res = await authApi.twoFactor.disableStart(password)
      if (res.data.twoFactorEnabled === false) {
        // Server turned it off directly (no phone) — refresh + done.
        await fetchUser()
        setDisablePhase('idle'); setPassword('')
        setInfo('Two-factor authentication is off.')
        return
      }
      setCode('')
      setDisablePhase('code')
      setInfo('We texted you a code to confirm.')
    } catch (err: any) {
      const ec = err?.response?.data?.error
      if (ec === 'wrong_password') setError('Incorrect password.')
      else if (ec === 'twofa_sms_failed') setError("Couldn't send the confirmation SMS. Try again.")
      else setError('Could not start disabling 2FA.')
    } finally { setBusy(false) }
  }

  const handleDisableConfirm = async () => {
    setError(''); setInfo(''); setBusy(true)
    try {
      await authApi.twoFactor.disable(code)
      await fetchUser()
      setDisablePhase('idle'); setPassword(''); setCode('')
      setInfo('Two-factor authentication is off.')
    } catch (err: any) {
      const ec = err?.response?.data?.error
      if (ec === 'wrong_code') setError(`Incorrect code.${typeof err?.response?.data?.attemptsLeft === 'number' ? ` ${err.response.data.attemptsLeft} attempts left.` : ''}`)
      else if (ec === 'code_expired') setError('That code expired. Start again.')
      else if (ec === 'too_many_attempts') setError('Too many attempts. Start again.')
      else setError('Could not disable 2FA.')
    } finally { setBusy(false) }
  }

  const revoke = async (id: string) => {
    setBusy(true)
    try {
      await authApi.twoFactor.revokeTrustedDevice(id)
      loadDevices()
    } catch { /* non-fatal */ } finally { setBusy(false) }
  }

  return (
    <div className="border-t border-border pt-6 mt-6">
      <h3 className="text-sm font-semibold text-foreground mb-1 flex items-center gap-2">
        <ShieldCheck size={16} className="text-primary" /> Two-factor authentication
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Require an SMS code when signing in. Uses the phone number from your profile.
      </p>

      {!enabled && (
        <div className="space-y-2">
          {!phoneVerified && (
            <p className="text-xs text-yellow-500">Add and verify a phone number in the Profile tab to enable 2FA.</p>
          )}
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy || !phoneVerified}
            className="inline-flex items-center gap-2 rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm font-medium hover:bg-primary/90 transition disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Enable 2FA
          </button>
        </div>
      )}

      {enabled && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 text-green-500 font-medium"><ShieldCheck size={15} /> Enabled</span>
            {disablePhase === 'idle' && (
              <button type="button" onClick={() => { setDisablePhase('password'); setError(''); setInfo('') }} className="ml-auto text-xs text-destructive hover:underline">Disable</button>
            )}
          </div>

          {disablePhase === 'password' && (
            <div className="space-y-2 max-w-sm">
              <p className="text-xs text-muted-foreground">Confirm your password to disable 2FA.</p>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Current password"
                autoFocus
                className="w-full bg-card text-foreground rounded px-3 py-2 text-sm border border-border focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleDisableStart} disabled={busy || !password} className="inline-flex items-center gap-2 rounded bg-destructive text-white px-3 py-1.5 text-sm font-medium hover:bg-destructive/90 transition disabled:opacity-50">
                  {busy && <Loader2 size={14} className="animate-spin" />} Continue
                </button>
                <button type="button" onClick={() => { setDisablePhase('idle'); setPassword(''); setError('') }} className="text-xs text-muted-foreground hover:text-foreground transition">Cancel</button>
              </div>
            </div>
          )}

          {disablePhase === 'code' && (
            <div className="space-y-2 max-w-sm">
              <p className="text-xs text-muted-foreground">Enter the code we texted you.</p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="123456"
                autoFocus
                className="w-full bg-card text-foreground rounded px-3 py-2.5 text-center text-lg tracking-[0.4em] border border-border focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleDisableConfirm} disabled={busy || code.length < 4} className="inline-flex items-center gap-2 rounded bg-destructive text-white px-3 py-1.5 text-sm font-medium hover:bg-destructive/90 transition disabled:opacity-50">
                  {busy && <Loader2 size={14} className="animate-spin" />} Disable 2FA
                </button>
                <button type="button" onClick={() => { setDisablePhase('idle'); setCode(''); setPassword(''); setError('') }} className="text-xs text-muted-foreground hover:text-foreground transition">Cancel</button>
              </div>
            </div>
          )}

          {/* Trusted devices */}
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-xs font-semibold text-foreground mb-2">Trusted devices</div>
            {devices.length === 0 ? (
              <p className="text-xs text-muted-foreground">No trusted devices. "Trust this device" at sign-in skips the code for 30 days.</p>
            ) : (
              <ul className="space-y-2">
                {devices.map((d) => (
                  <li key={d.id} className="flex items-center gap-2 text-xs">
                    <Smartphone size={13} className="text-muted-foreground shrink-0" />
                    <span className="text-foreground">{d.label || 'Unknown device'}</span>
                    <span className="text-muted-foreground">· last used {new Date(d.lastUsedAt).toLocaleDateString()}</span>
                    <button type="button" onClick={() => revoke(d.id)} disabled={busy} className="ml-auto inline-flex items-center gap-1 text-destructive hover:underline disabled:opacity-50">
                      <Trash2 size={12} /> Revoke
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {error && <p className="text-destructive text-xs mt-2">{error}</p>}
      {!error && info && <p className="text-green-500 text-xs mt-2">{info}</p>}
    </div>
  )
}
